import PropTypes from "prop-types";
import isMobile from "../../../utils/common";
import ImageTile from "../../atoms/imageTile";
/**
 * DeliveryDateSelector
 *
 * A component for selecting a delivery date, used across product, cart, and delivery address pages.
 * Displays a placeholder or selected value, and supports error/info states with optional adornments.
 *
 * @component
 * @param {object} props - Component props
 * @param {string} props.value - Currently selected delivery date value
 * @param {string} props.errorMsg - Error message to show when delivery date is invalid
 * @param {string} props.infoMsg - Info message to show (e.g. when a time slot expires)
 * @param {string} props.errorStyleType - Determines the visual style for error/info messages ('animate' or 'static')
 * @param {boolean} props.isErrorMsgAnimate - Whether to animate the error message
 * @param {string} props.label - Optional label for the component
 * @param {Function} props.onClick - Function to call when the component is clicked
 * @param {boolean} props.isDisabled - Whether the component is disabled
 * @param {React.ReactNode} props.endAdornment - Element to display on the right side (e.g. arrow icon)
 * @param {React.ReactNode} props.startAdornment - Element to display on the left side (e.g. custom icon)
 * @param {boolean} props.isInFocus - Whether the component is currently focused
 * @param {string} props.wrapperClassName - Additional class names for the wrapper
 * @returns {JSX.Element} The rendered component
 */
const DeliveryDateSelector = ({
  value,
  errorMsg,
  infoMsg,
  errorStyleType,
  isErrorMsgAnimate,
  label,
  onClickHandler,
  isDisabled,
  endAdornment,
  startAdornment,
  isInFocus,
  wrapperClassName,
}) => {
  const borderStyle = errorMsg
    ? errorStyleType == "animate"
      ? "border-red border animate-glowing"
      : "border-red border"
    : infoMsg
      ? "border-blue border"
      : isInFocus
        ? "border-grey border"
        : "border-greySoft border";

  return (
    <div className="relative flex w-full flex-col" id="delivery_date_slot">
      <button
        type="button"
        data-testid="delivery_date_selector"
        disabled={isDisabled}
        onClick={onClickHandler}
        className={`flex w-[100%] items-center justify-between rounded-lg ${isMobile() ? "p-8" : "p-12"} ${borderStyle} ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} ${wrapperClassName}`}
      >
        <div className={`flex ${isMobile() ? "gap-6" : "gap-16"}`}>
          <div className="flex items-start">
            <div className="flex h-fit min-h-fit w-fit min-w-fit items-center">{startAdornment}</div>
          </div>
          <div className="align-center flex items-center">{value}</div>
        </div>

        <div className="flex items-center">{endAdornment}</div>
      </button>
      {label && (
        <div className="absolute left-40 top-[-8px] flex bg-[#ffffff] px-[5px] text-12 font-medium text-grey-500">
          {label}
        </div>
      )}

      {(errorMsg || infoMsg) && (
        <div>
          <span
            data-testid="input_related_message"
            className={`mt-10 flex w-fit ${isErrorMsgAnimate ? "animate-shake" : ""} items-center rounded-4 p-4 text-sm font-500 tracking-wide text-grey-500 ${
              errorMsg ? "bg-red-100 bg-opacity-5" : ""
            }`}
          >
            <div>
              <ImageTile src="/icons/warning.svg" width={16} height={16} alt="" sizes="10vw" isPriority={false} />
            </div>

            <p className="ml-4">{errorMsg || infoMsg}</p>
          </span>
        </div>
      )}
    </div>
  );
};
DeliveryDateSelector.propTypes = {
  value: PropTypes.object,
  errorMsg: PropTypes.string,
  infoMsg: PropTypes.string,
  errorStyleType: PropTypes.string,
  isErrorMsgAnimate: PropTypes.bool,
  label: PropTypes.string,
  onClickHandler: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  endAdornment: PropTypes.node,
  startAdornment: PropTypes.node,
  isInFocus: PropTypes.bool,
  wrapperClassName: PropTypes.string,
};

export default DeliveryDateSelector;
